import React from 'react';
import { Card, Table, Tag, Space, Button, Modal, message, Tabs, Input } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { API_BASE_URL } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

interface Approval {
  id: number;
  userId: number;
  type: 'customer' | 'supplier' | 'support' | 'delivery' | 'employee';
  companyName: string;
  inn: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  status: 'pending' | 'approved' | 'rejected';
  approverType: string;
  approverId?: number;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string;
  reviewedBy?: number;
  user: {
    id: number;
    email: string;
    username: string;
    role: string;
    isActive: boolean;
  };
}

type AdminApprovalsProps = {
  /** Узкий режим для Drawer на странице «Мои доставщики» */
  embedded?: boolean;
};

const AdminApprovals: React.FC<AdminApprovalsProps> = ({ embedded = false }) => {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const [loading, setLoading] = React.useState(false);
  const [approvals, setApprovals] = React.useState<Approval[]>([]);
  const [tab, setTab] = React.useState<'pending' | 'approved' | 'rejected'>('pending');
  const [selected, setSelected] = React.useState<Approval | null>(null);
  const [detailsVisible, setDetailsVisible] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [editData, setEditData] = React.useState<Partial<Approval>>({});
  const [rejectComment, setRejectComment] = React.useState('');
  const [rejectModalVisible, setRejectModalVisible] = React.useState(false);
  const [selectedForReject, setSelectedForReject] = React.useState<Approval | null>(null);
  const [revertModalVisible, setRevertModalVisible] = React.useState(false);
  const [selectedForRevert, setSelectedForRevert] = React.useState<Approval | null>(null);
  const [revertAction, setRevertAction] = React.useState<'pending' | 'approved'>('pending');

  const [searchText, setSearchText] = React.useState('');

  // Проверяем, может ли пользователь откатывать отклонения
  const canRevertRejections = () => {
    if (!user) return false;
    // Только админ, поставщик и заказчик могут откатывать отклонения
    return ['admin', 'supplier', 'customer'].includes(user.role);
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/approvals`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApprovals(data);
      } else {
        throw new Error('Ошибка загрузки заявок');
      }
    } catch (error) {
      message.error('Ошибка загрузки заявок: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const displayName = (a: Approval) =>
    a.companyName || a.contactName || a.user?.username || `пользователь #${a.userId}`;

  const handleApprove = async (approval: Approval) => {
    Modal.confirm({
      title: 'Одобрить заявку',
      content: `Вы уверены, что хотите одобрить регистрацию «${displayName(approval)}»?`,
      okText: 'Одобрить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/approvals/${approval.id}/approve`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
          });
          
          if (response.ok) {
            message.success('Заявка одобрена');
            setDetailsVisible(false);
            load(); // Перезагружаем данные
          } else {
            throw new Error('Ошибка при одобрении заявки');
          }
        } catch (error) {
          message.error('Ошибка при одобрении заявки: ' + error.message);
        }
      }
    });
  };

  const handleReject = (approval: Approval) => {
    setSelectedForReject(approval);
    setRejectComment('');
    setRejectModalVisible(true);
  };

  const confirmReject = async () => {
    if (!selectedForReject) return;
    
    if (!rejectComment.trim()) {
      message.error('Пожалуйста, укажите причину отклонения');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Токен авторизации не найден. Пожалуйста, войдите в систему заново.');
        return;
      }

      // Проверяем, что токен не поврежден
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }
        // Пытаемся декодировать payload (без проверки подписи)
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Token payload:', payload);
      } catch (tokenError) {
        console.error('Token validation error:', tokenError);
        message.error('Токен авторизации поврежден. Пожалуйста, войдите в систему заново.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/approvals/${selectedForReject.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment: rejectComment
        })
      });
      
      if (response.ok) {
        message.success('Заявка отклонена');
        setDetailsVisible(false);
        setRejectModalVisible(false);
        setRejectComment('');
        setSelectedForReject(null);
        load(); // Перезагружаем данные
      } else {
        const errorData = await response.json();
        console.error('Reject error details:', errorData);
        throw new Error(`Ошибка при отклонении заявки: ${errorData.error || errorData.message || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      message.error('Ошибка при отклонении заявки: ' + error.message);
    }
  };

  const handleRevert = (approval: Approval) => {
    setSelectedForRevert(approval);
    setRevertAction('pending');
    setRevertModalVisible(true);
  };

  const confirmRevert = async () => {
    if (!selectedForRevert) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/approvals/${selectedForRevert.id}/revert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: revertAction
        })
      });
      
      if (response.ok) {
        message.success(`Заявка возвращена в статус "${revertAction === 'pending' ? 'ожидает' : 'одобрена'}"`);
        setRevertModalVisible(false);
        setSelectedForRevert(null);
        load(); // Перезагружаем данные
      } else {
        throw new Error('Ошибка при откате отклонения');
      }
    } catch (error) {
      message.error('Ошибка при откате отклонения: ' + error.message);
    }
  };

  const startEdit = () => {
    if (selected) {
      setEditData(selected);
      setEditMode(true);
    }
  };

  const saveEdit = () => {
    if (!selected) return;
    
    // Проверить, были ли изменения
    const changes: Array<{ field: string; before: any; after: any }> = [];
    
    if (editData.companyName !== selected.companyName) {
      changes.push({
        field: 'Название компании',
        before: selected.companyName,
        after: editData.companyName
      });
    }
    
    if (editData.inn !== selected.inn) {
      changes.push({
        field: 'ИНН',
        before: selected.inn,
        after: editData.inn
      });
    }
    
    if (editData.contactName !== selected.contactName) {
      changes.push({
        field: 'Контактное лицо',
        before: selected.contactName,
        after: editData.contactName
      });
    }
    
    if (editData.contactPhone !== selected.contactPhone) {
      changes.push({
        field: 'Телефон',
        before: selected.contactPhone,
        after: editData.contactPhone
      });
    }
    
    if (editData.contactEmail !== selected.contactEmail) {
      changes.push({
        field: 'Email',
        before: selected.contactEmail,
        after: editData.contactEmail
      });
    }
    
    if (changes.length > 0) {
      Modal.info({
        title: 'Изменения сохранены',
        content: (
          <div>
            <p>Были изменены следующие поля:</p>
            <ul>
              {changes.map((change, index) => (
                <li key={index}>
                  <strong>{change.field}:</strong> {change.before} → {change.after}
                </li>
              ))}
            </ul>
          </div>
        ),
        width: 500
      });
    } else {
      message.info('Изменений не было');
    }
    
    setEditMode(false);
    setEditData({});
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'pending':
        return <Tag color="orange">Ожидает</Tag>;
      case 'approved':
        return <Tag color="green">Одобрено</Tag>;
      case 'rejected':
        return <Tag color="red">Отклонено</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getTypeTag = (type: string) => {
    switch (type) {
      case 'customer':
        return <Tag color="blue">Заказчик</Tag>;
      case 'supplier':
        return <Tag color="green">Поставщик</Tag>;
      case 'support':
        return <Tag color="purple">Поддержка</Tag>;
      case 'delivery':
        return <Tag color="orange">Доставка</Tag>;
      case 'employee':
        return <Tag color="cyan">Сотрудник</Tag>;
      case 'customer_manager':
        return <Tag color="purple">Контролёр заказчика</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  const filteredApprovals = approvals.filter(approval => {
    const matchesTab = tab === 'pending' ? approval.status === 'pending' :
                      tab === 'approved' ? approval.status === 'approved' :
                      approval.status === 'rejected';
    
    const matchesSearch = !searchText || 
      approval.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
      approval.inn.includes(searchText) ||
      approval.contactName.toLowerCase().includes(searchText.toLowerCase());
    
    return matchesTab && matchesSearch;
  });

  const columns = [
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => getTypeTag(type),
      sorter: (a: Approval, b: Approval) => a.type.localeCompare(b.type)
    },
    {
      title: 'Название компании',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text: string, record: Approval) => (
        <Button 
          type="link" 
          onClick={() => {
            setSelected(record);
            setDetailsVisible(true);
          }}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
      sorter: (a: Approval, b: Approval) => a.companyName.localeCompare(b.companyName)
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
      width: 120,
      render: (text: string, record: Approval) => (
        <Button 
          type="link" 
          onClick={() => {
            setSelected(record);
            setDetailsVisible(true);
          }}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Контактное лицо',
      dataIndex: 'contactName',
      key: 'contactName',
      render: (text: string) => <span style={{ fontSize: '13px' }}>{text}</span>
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
      sorter: (a: Approval, b: Approval) => a.status.localeCompare(b.status)
    },
    {
      title: 'Дата заявки',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => (
        <span style={{ fontSize: '13px', color: '#666' }}>
          {new Date(date).toLocaleDateString('ru')}
        </span>
      ),
      sorter: (a: Approval, b: Approval) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (text: string, record: Approval) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record)}
            disabled={record.status !== 'pending'}
          >
            Одобрить
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<CloseOutlined />}
            onClick={() => handleReject(record)}
            disabled={record.status !== 'pending'}
          >
            Отклонить
          </Button>
          <Button 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => {
              setSelected(record);
              setDetailsVisible(true);
            }}
          >
            Просмотр
          </Button>
          {record.status === 'rejected' && canRevertRejections() && (
            <Button 
              type="default" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleRevert(record)}
              style={{ color: '#1890ff' }}
            >
              Откатить
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: embedded ? 0 : '24px' }}>
      <Card 
        title={embedded ? 'Заявки на регистрацию' : 'Заявки на согласование'} 
        styles={{ body: { padding: embedded ? '12px' : '20px' } }}
        extra={
          <Space>
            <Input.Search
              placeholder="Поиск по названию, ИНН, контакту"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </Space>
        }
      >
        <Tabs 
          activeKey={tab} 
          onChange={(key) => setTab(key as any)}
          items={[
            {
              key: 'pending',
              label: `Ожидают (${approvals.filter(a => a.status === 'pending').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredApprovals}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'approved',
              label: `Одобрены (${approvals.filter(a => a.status === 'approved').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredApprovals}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'rejected',
              label: `Отклонены (${approvals.filter(a => a.status === 'rejected').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredApprovals}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            }
          ]}
        />
      </Card>

      {detailsVisible && selected && (
        <Modal
          title="Детали заявки"
          open={detailsVisible}
          onCancel={() => {
            setDetailsVisible(false);
            setEditMode(false);
            setEditData({});
          }}
          footer={null}
          width={isMobile ? '100%' : 800}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
        >
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px' }}>{selected.companyName}</h3>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {getTypeTag(selected.type)} • {getStatusTag(selected.status)}
                </div>
              </div>
              {!editMode && (
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={startEdit}
                >
                  Редактировать
                </Button>
              )}
            </div>

            {editMode ? (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Название компании:</label>
                  <Input 
                    value={editData.companyName || selected.companyName}
                    onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>ИНН:</label>
                  <Input 
                    value={editData.inn || selected.inn}
                    onChange={(e) => setEditData({ ...editData, inn: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Контактное лицо:</label>
                  <Input 
                    value={editData.contactName || selected.contactName}
                    onChange={(e) => setEditData({ ...editData, contactName: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Телефон:</label>
                  <Input 
                    value={editData.contactPhone || selected.contactPhone}
                    onChange={(e) => setEditData({ ...editData, contactPhone: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Email:</label>
                  <Input 
                    value={editData.contactEmail || selected.contactEmail}
                    onChange={(e) => setEditData({ ...editData, contactEmail: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button type="primary" icon={<SaveOutlined />} onClick={saveEdit}>
                    Сохранить
                  </Button>
                  <Button onClick={cancelEdit}>
                    Отмена
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Название компании</div>
                    <div style={{ fontSize: '14px' }}>{selected.companyName}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>ИНН</div>
                    <div style={{ fontSize: '14px' }}>{selected.inn}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Контактное лицо</div>
                    <div style={{ fontSize: '14px' }}>{selected.contactName}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Телефон</div>
                    <div style={{ fontSize: '14px' }}>{selected.contactPhone}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Email</div>
                    <div style={{ fontSize: '14px' }}>{selected.contactEmail}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Статус</div>
                    <div style={{ fontSize: '14px' }}>{getStatusTag(selected.status)}</div>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Дата заявки</div>
                    <div style={{ fontSize: '14px' }}>{new Date(selected.createdAt).toLocaleString('ru')}</div>
                  </div>
                  {selected.reviewedAt && (
                    <>
                      <div>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Дата ответа</div>
                        <div style={{ fontSize: '14px' }}>{new Date(selected.reviewedAt).toLocaleString('ru')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>Ответил</div>
                        <div style={{ fontSize: '14px' }}>ID: {selected.reviewedBy}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {selected.status === 'pending' && (
            <div style={{ 
              borderTop: '1px solid #f0f0f0', 
              paddingTop: '20px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button 
                type="primary" 
                icon={<CheckOutlined />}
                onClick={() => handleApprove(selected)}
              >
                Одобрить
              </Button>
              <Button 
                danger 
                icon={<CloseOutlined />}
                onClick={() => handleReject(selected)}
              >
                Отклонить
              </Button>
            </div>
          )}
        </Modal>
      )}

      {/* Модальное окно для отклонения с комментарием */}
      {rejectModalVisible && selectedForReject && (
        <Modal
          title="Отклонить заявку"
          open={rejectModalVisible}
          onCancel={() => {
            setRejectModalVisible(false);
            setRejectComment('');
            setSelectedForReject(null);
          }}
          onOk={confirmReject}
          okText="Отклонить"
          cancelText="Отмена"
          okButtonProps={{ danger: true }}
          width={600}
        >
          <div style={{ marginBottom: '16px' }}>
            <p>Вы собираетесь отклонить заявку от <strong>{selectedForReject.companyName}</strong></p>
            <p>Комментарий будет отправлен заявителю на email: <strong>{selectedForReject.user.email}</strong></p>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Причина отклонения (обязательно):
            </label>
            <Input.TextArea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Укажите причину отклонения заявки..."
              rows={4}
              style={{ width: '100%' }}
            />
          </div>
        </Modal>
      )}

      {/* Модальное окно для отката отклонения */}
      {revertModalVisible && selectedForRevert && (
        <Modal
          title="Откатить отклонение"
          open={revertModalVisible}
          onCancel={() => {
            setRevertModalVisible(false);
            setSelectedForRevert(null);
          }}
          onOk={confirmRevert}
          okText="Откатить"
          cancelText="Отмена"
          width={500}
        >
          <div style={{ marginBottom: '16px' }}>
            <p>Вы собираетесь откатить отклонение заявки от <strong>{selectedForRevert.companyName}</strong></p>
            <p>Выберите новый статус для заявки:</p>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Новый статус:
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="revertAction"
                  value="pending"
                  checked={revertAction === 'pending'}
                  onChange={(e) => setRevertAction(e.target.value as 'pending')}
                  style={{ marginRight: '8px' }}
                />
                <span>Ожидает (вернуть на рассмотрение)</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="revertAction"
                  value="approved"
                  checked={revertAction === 'approved'}
                  onChange={(e) => setRevertAction(e.target.value as 'approved')}
                  style={{ marginRight: '8px' }}
                />
                <span>Одобрена (сразу одобрить)</span>
              </label>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminApprovals;